import { Outlet, useParams } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { InternalPartnerSidebar } from "@/components/partners/InternalPartnerSidebar";
import { PartnerNotificationBell } from "@/components/partners/PartnerNotificationBell";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSelector } from "@/components/ui/language-selector";
import { RoleSwitcher } from "@/components/layout/RoleSwitcher";
import { SignOutButton } from "@/components/layout/SignOutButton";
import { Scale, Plane, FileCheck, Calculator, BarChart3, Users, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const partnerConfig: Record<string, { icon: any; title: { en: string; fr: string }; subtitle: { en: string; fr: string } }> = {
  "business-law": {
    icon: Scale,
    title: { en: "Business Law Portal", fr: "Portail Droit des Affaires" },
    subtitle: { en: "Corporate Legal Services Management", fr: "Gestion des Services Juridiques Corporatifs" }
  },
  "immigration-law": {
    icon: Plane,
    title: { en: "Immigration Law Portal", fr: "Portail Droit de l'Immigration" },
    subtitle: { en: "Immigration Services Management", fr: "Gestion des Services d'Immigration" }
  },
  "notary": {
    icon: FileCheck,
    title: { en: "Notary Portal", fr: "Portail Notaire" },
    subtitle: { en: "Notarial Services Management", fr: "Gestion des Services Notariaux" }
  },
  "tax-advisor": {
    icon: Calculator,
    title: { en: "Tax Advisor Portal", fr: "Portail Conseiller Fiscal" },
    subtitle: { en: "Tax Advisory Services Management", fr: "Gestion des Services de Conseil Fiscal" }
  },
  "cpa": {
    icon: BarChart3,
    title: { en: "CPA Portal", fr: "Portail CPA" },
    subtitle: { en: "Accounting & Financial Services Management", fr: "Gestion des Services Comptables et Financiers" }
  }
};

export default function InternalPartnerLayout() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const config = partnerType ? partnerConfig[partnerType] : null;
  const Icon = config?.icon || Users;
  const title = config?.title?.[language as keyof typeof config.title] || config?.title?.en || "Partner Portal";
  const subtitle = config?.subtitle?.[language as keyof typeof config.subtitle] || config?.subtitle?.en || "Professional Services Management";

  return (
    <div className="min-h-screen bg-background">
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <InternalPartnerSidebar partnerType={partnerType} />
          <div className="flex-1 flex flex-col">
            <header className="border-b bg-background flex items-center justify-between px-6 sticky top-0 z-10" style={{ minHeight: '72px' }}>
              <div className="flex items-center gap-4">
                <SidebarTrigger className="mr-2" />
                <img src="/noBgColor.png" alt="AcquizGroup" className="h-10" />
                <div className="flex items-center gap-3 border-l pl-4">
                  <Icon className="h-6 w-6 text-primary" />
                  <div>
                    <h1 className="text-lg font-semibold">{title}</h1>
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <RoleSwitcher />
                <LanguageSelector />
                <PartnerNotificationBell />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/partners/profile')}
                  title={language === 'fr' ? 'Mon Profil' : 'My Profile'}
                >
                  <User className="h-5 w-5" />
                </Button>
                <SignOutButton />
              </div>
            </header>
            <main className="flex-1 p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
